'use client'

/**
 * Media Mutation Hooks — generic factories for CRUD on media entities.
 *
 * Each kind contributes a draft and a change; `document/operations/` performs them. This file held
 * 15 of the 17 whole-`metadata` spreads #108 names, and they are gone rather than rewritten: an
 * update now describes *what changed*, and `patch.ts` writes each key into the map that is already
 * there. There is no longer an operand to spread.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  useDocumentOps,
  type EntryChange,
  type MetadataPatch,
  useDocumentView,
} from '@/document'
import type {
  ArtistUpdate,
  LabelUpdate,
  MovieUpdate,
  BookUpdate,
  ReleaseUpdate,
  // The five **update** drafts are still the Supabase row shapes. They are the
  // wall's, not the document's, and #130 decides whether they retire with it.
  // The five create drafts left this import on #401: the capture form was
  // reaching `MovieInsert` and its four siblings through here to build them.
  // eslint-disable-next-line import/no-restricted-paths
} from '@/legacy/supabase-store'
import { libraryQueryKey } from './select-library'
import type { MediaKind } from './meta'
import type { EntryDraft, EntryType } from '@/document'

interface CreateSpec<T> {
  type: EntryType | 'album'
  topicType: EntryType | 'album'
  build: (d: T) => MediaBuild
}

/** What a media kind's `build` produces: the parts of an `EntryDraft` that kind decides. */
interface MediaBuild {
  title: string
  content: string
  imageUrl?: string
  metadata: Record<string, unknown>
  genres?: string[]
}

const invalidate = (qc: ReturnType<typeof useQueryClient>) => () => {
  qc.invalidateQueries({ queryKey: libraryQueryKey })
}

function useCreateEntity<T>(spec: CreateSpec<T>) {
  const doc = useDocumentView()
  const ops = useDocumentOps()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: T) => {
      if (!doc) throw new Error('Document not ready')
      const b = spec.build(data)
      const id = ops.createEntry({
        type: spec.type,
        title: b.title,
        content: b.content,
        imageUrl: b.imageUrl,
        metadata: b.metadata,
        genres: b.genres?.length
          ? { names: b.genres, scope: spec.topicType }
          : undefined,
      })
      return doc.entries[id]
    },
    onSuccess: invalidate(qc),
  })
}

function useUpdateEntity<T>(label: string, toChange: (d: T) => EntryChange) {
  const doc = useDocumentView()
  const ops = useDocumentOps()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: T }) => {
      if (!doc) throw new Error('Document not ready')
      if (!ops.updateEntry(id, toChange(data))) {
        throw new Error(`${label} ${id} not found`)
      }
      return doc.entries[id]
    },
    onSuccess: invalidate(qc),
  })
}

function useDeleteEntity(label: string) {
  const doc = useDocumentView()
  const ops = useDocumentOps()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      if (!doc) throw new Error('Document not ready')
      if (!ops.archiveEntry(id)) throw new Error(`${label} ${id} not found`)
    },
    onSuccess: invalidate(qc),
  })
}

/**
 * The three fields every media kind shares: a title, a body, and an image cleared by passing
 * nothing. The image is an entry field and a metadata key on some kinds, which is why the metadata
 * copy is each kind's business rather than this helper's.
 */
function baseChange(
  title: string | undefined,
  notes: string | null | undefined,
  imageUrl: string | null | undefined,
  metadata: MetadataPatch,
): EntryChange {
  const change: EntryChange = { metadata }
  if (title !== undefined) change.title = title
  if (notes !== undefined) change.content = notes || ''
  if (imageUrl !== undefined) change.imageUrl = imageUrl || undefined
  return change
}

// Movie

/**
 * What the five media forms edit.
 *
 * **One draft for five kinds**, which is the same claim as one implementation: `mediaSlice<K>()` is
 * bound five times and each binding takes this shape. It is the union of what the five rows'
 * `onSave` payloads carried between them — `director` is the movie's, `author` the book's, `bio`
 * and `website` the artist's and label's — rather than five drafts that would need five slices.
 *
 * `name` and `title` are both here because the three music rows edit `name` while the movie and
 * book rows edit `title`, and both write `entry.title`. Reconciling them is a rename in the forms,
 * not something a move gets to do.
 */
export interface MediaDraft {
  title?: string
  name?: string
  director?: string | null
  author?: string | null
  year?: number | null
  overview?: string | null
  description?: string | null
  bio?: string | null
  profile?: string | null
  website?: string | null
  notes?: string | null
  status?: string
  topics?: string[]
}

// ============================================================================
// What creating each media kind takes
// ============================================================================

/**
 * The five create drafts, declared here rather than borrowed from the wall.
 *
 * Step 2 left the creates taking `MovieInsert` and its four siblings out of
 * `legacy/supabase-store`, and the capture form was building those Supabase row shapes through five
 * `xCreateDataToInsert` adapters (#401). Each field below is one the matching `build` reads, so
 * these are what the slice actually consumes rather than what a retired table happened to declare.
 * The snake_case survives because the mapping that fills them still speaks it; renaming it is a
 * rename in the mapping, not something this move gets to do.
 */
export interface MovieCreate {
  title: string
  director?: string
  genres?: string[]
  year?: number
  status?: string
  rating?: number | null
  notes?: string | null
  external_id?: string
  poster_url?: string
  overview?: string
}

export interface BookCreate {
  title: string
  author?: string
  topics?: string[]
  year?: number
  status?: string
  rating?: number | null
  notes?: string | null
  external_id?: string
  cover_url?: string
  description?: string
}

export interface ArtistCreate {
  name: string
  genres?: string[]
  notes?: string | null
  external_id?: string
  image_url?: string
  bio?: string
}

export interface LabelCreate {
  name: string
  genres?: string[]
  website?: string
  notes?: string | null
  external_id?: string
  image_url?: string
  profile?: string
}

export interface ReleaseCreate {
  title: string
  genres?: string[]
  year?: number
  notes?: string | null
  external_id?: string
  cover_url?: string
}

const movieSpec: CreateSpec<MovieCreate> = {
  type: 'movie',
  topicType: 'movie',
  build: (d) => ({
    title: d.title,
    content: d.notes || '',
    imageUrl: d.poster_url || undefined,
    genres: d.genres,
    metadata: {
      director: d.director,
      year: d.year,
      status: d.status,
      rating: d.rating,
      posterUrl: d.poster_url,
      overview: d.overview,
    },
  }),
}

export function useCreateMovie() {
  return useCreateEntity(movieSpec)
}

export function useUpdateMovie() {
  return useUpdateEntity<MovieUpdate>('Movie', (d) => {
    const metadata: MetadataPatch = {}
    if (d.director !== undefined) metadata.director = d.director
    if (d.year !== undefined) metadata.year = d.year
    if (d.status !== undefined) metadata.status = d.status
    if (d.rating !== undefined) metadata.rating = d.rating
    if (d.poster_url !== undefined) metadata.posterUrl = d.poster_url
    if (d.overview !== undefined) metadata.overview = d.overview

    const change = baseChange(d.title, d.notes, d.poster_url, metadata)
    if (d.genres !== undefined) {
      change.genres = { names: d.genres, scope: 'movie' }
    }
    return change
  })
}

export const useDeleteMovie = () => useDeleteEntity('Movie')

// Book

const bookSpec: CreateSpec<BookCreate> = {
  type: 'book',
  topicType: 'book',
  build: (d) => ({
    title: d.title,
    content: d.notes || '',
    imageUrl: d.cover_url || undefined,
    genres: d.topics,
    metadata: {
      author: d.author,
      topics: d.topics || [],
      year: d.year,
      status: d.status,
      description: d.description,
    },
  }),
}

export function useCreateBook() {
  return useCreateEntity(bookSpec)
}

export function useUpdateBook() {
  return useUpdateEntity<BookUpdate>('Book', (d) => {
    const metadata: MetadataPatch = {}
    if (d.author !== undefined) metadata.author = d.author
    if (d.topics !== undefined) metadata.topics = d.topics
    if (d.year !== undefined) metadata.year = d.year
    if (d.status !== undefined) metadata.status = d.status
    if (d.description !== undefined) metadata.description = d.description

    return baseChange(d.title, d.notes, d.cover_url, metadata)
  })
}

export const useDeleteBook = () => useDeleteEntity('Book')

// Artist

const artistSpec: CreateSpec<ArtistCreate> = {
  type: 'artist',
  topicType: 'artist',
  build: (d) => ({
    title: d.name,
    content: d.notes || '',
    imageUrl: d.image_url || undefined,
    genres: d.genres,
    metadata: { bio: d.bio },
  }),
}

export function useCreateArtist() {
  return useCreateEntity(artistSpec)
}

export function useUpdateArtist() {
  return useUpdateEntity<ArtistUpdate>('Artist', (d) => {
    const metadata: MetadataPatch = {}
    if (d.bio !== undefined) metadata.bio = d.bio

    const change = baseChange(d.name, d.notes, d.image_url, metadata)
    if (d.genres !== undefined) {
      change.genres = { names: d.genres, scope: 'artist' }
    }
    return change
  })
}

export const useDeleteArtist = () => useDeleteEntity('Artist')

// Label

const labelSpec: CreateSpec<LabelCreate> = {
  type: 'label',
  topicType: 'label',
  build: (d) => ({
    title: d.name,
    content: d.notes || '',
    imageUrl: d.image_url || undefined,
    genres: d.genres,
    metadata: { website: d.website, profile: d.profile },
  }),
}

export function useCreateLabel() {
  return useCreateEntity(labelSpec)
}

export function useUpdateLabel() {
  return useUpdateEntity<LabelUpdate>('Label', (d) => {
    const metadata: MetadataPatch = {}
    if (d.website !== undefined) metadata.website = d.website
    if (d.profile !== undefined) metadata.profile = d.profile

    const change = baseChange(d.name, d.notes, d.image_url, metadata)
    if (d.genres !== undefined) {
      change.genres = { names: d.genres, scope: 'label' }
    }
    return change
  })
}

export const useDeleteLabel = () => useDeleteEntity('Label')

// Release

const releaseSpec: CreateSpec<ReleaseCreate> = {
  type: 'album',
  topicType: 'album',
  build: (d) => ({
    title: d.title,
    content: d.notes || '',
    imageUrl: d.cover_url || undefined,
    genres: d.genres,
    metadata: { year: d.year },
  }),
}

export function useCreateRelease() {
  return useCreateEntity(releaseSpec)
}

export function useUpdateRelease() {
  return useUpdateEntity<ReleaseUpdate>('Release', (d) => {
    const metadata: MetadataPatch = {}
    if (d.year !== undefined) metadata.year = d.year

    const change = baseChange(d.title, d.notes, d.cover_url, metadata)
    if (d.genres !== undefined) {
      change.genres = { names: d.genres, scope: 'album' }
    }
    return change
  })
}

export const useDeleteRelease = () => useDeleteEntity('Release')

// ============================================================================
// The mapping, without the mutation
// ============================================================================

const CREATE_SPECS = {
  movie: movieSpec,
  book: bookSpec,
  artist: artistSpec,
  label: labelSpec,
  release: releaseSpec,
} as const

/**
 * One media create draft onto one `EntryDraft`, with no React and no mutation.
 *
 * The five `build` functions above are the only place that says what a movie's metadata is, and
 * until #402 the external capture path said it a second time — a hand-written six-case `switch` in
 * `EntryUpdateProvider.tsx` that disagreed with them on half the keys. That switch calls this now,
 * so the mapping is one implementation with two callers rather than two implementations.
 *
 * `release` writes `type: 'album'`, which is the live ninth kind #395 owns; preserved here rather
 * than corrected, because it decides what existing documents say.
 */
export function mediaEntryDraft(
  kind: MediaKind,
  data: MediaCreateInput,
): EntryDraft {
  const spec = CREATE_SPECS[kind]
  const b = spec.build(data as never)
  return {
    type: spec.type,
    title: b.title,
    content: b.content,
    imageUrl: b.imageUrl,
    metadata: b.metadata,
    ...(b.genres?.length
      ? { genres: { names: b.genres, scope: spec.topicType } }
      : {}),
  }
}

/** The five create drafts, as one union — what `mediaEntryDraft` accepts. */
export type MediaCreateInput =
  | MovieCreate
  | BookCreate
  | ArtistCreate
  | LabelCreate
  | ReleaseCreate
